import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, Settings, User, KeyRound, ChevronDown } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import GlobalLoader from './GlobalLoader';
import ProfileModal from '../../features/users-roles/components/modals/ProfileModal';
import ChangePasswordModal from '../../features/users-roles/components/modals/ChangePasswordModal';
import SettingsModal from '../../features/users-roles/components/modals/SettingsModal';

function getInitials(name) {
  if (!name) return 'U';
  const parts = name.trim().split(/\s+/);
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? parts[parts.length - 1]?.[0] ?? '' : '';
  return (first + last).toUpperCase();
}

export default function UserDropdown() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const containerRef = useRef(null);

  const displayName = user?.FullName || user?.name || 'Admin';
  const role = user?.Position || user?.Roles?.[0]?.RoleName || 'admin';
  const initials = getInitials(displayName);

  const handleToggle = useCallback(() => setOpen((v) => !v), []);
  const handleClose = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const handleLogout = async () => {
    setLoggingOut(true);
    try {
      await logout();
    } finally {
      navigate('/login', { replace: true });
    }
  };

  const menuItems = [
    {
      label: 'Profile',
      icon: User,
      onClick: () => {
        handleClose();
        setProfileOpen(true);
      },
    },
    {
      label: 'Change Password',
      icon: KeyRound,
      onClick: () => {
        handleClose();
        setPasswordOpen(true);
      },
    },
    {
      label: 'Settings',
      icon: Settings,
      onClick: () => {
        handleClose();
        setSettingsOpen(true);
      },
    },
    {
      divider: true,
    },
    {
      label: 'Logout',
      icon: LogOut,
      danger: true,
      onClick: () => {
        handleClose();
        handleLogout();
      },
    },
  ];

  return (
    <>
      <div ref={containerRef} className="relative">
        {/* Trigger */}
        <button
          onClick={handleToggle}
          className="flex items-center space-x-3 pl-4 border-l border-gray-200 focus:outline-none"
          aria-haspopup="true"
          aria-expanded={open}
        >
          <div className="w-9 h-9 rounded-full bg-brand-navy flex items-center justify-center text-white text-sm font-bold shadow-sm">
            {initials}
          </div>
          <div className="hidden md:block text-left">
            <p className="text-[15px] font-semibold text-brand-navy leading-tight">{displayName}</p>
            <p className="text-xs text-brand-navy/50 capitalize leading-tight">{role}</p>
          </div>
          <ChevronDown
            className={`w-4 h-4 text-brand-navy/40 hidden md:block transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
          />
        </button>

        {/* Dropdown */}
        <div
          className={`absolute right-0 mt-2 w-60 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden transition-all duration-200 origin-top-right z-50 ${
            open ? 'opacity-100 scale-100 translate-y-0 pointer-events-auto' : 'opacity-0 scale-95 -translate-y-1 pointer-events-none'
          }`}
        >
          {/* Header */}
          <div className="px-4 py-3 border-b border-gray-100 bg-brand-gray-light/50">
            <p className="text-[15px] font-semibold text-brand-navy">{displayName}</p>
            <p className="text-xs text-brand-navy/50 capitalize">{role}</p>
          </div>

          {/* Menu Items */}
          <div className="py-1">
            {menuItems.map((item, idx) =>
              item.divider ? (
                <div key={idx} className="my-1 border-t border-gray-100" />
              ) : (
                <button
                  key={idx}
                  onClick={item.onClick}
                  className={`w-full flex items-center px-4 py-2.5 text-[15px] transition-colors ${
                    item.danger
                      ? 'text-red-600 hover:bg-red-50'
                      : 'text-brand-navy hover:bg-brand-cream/40 hover:text-brand-gold'
                  }`}
                >
                  <item.icon className={`w-4 h-4 mr-3 flex-shrink-0 ${item.danger ? 'text-red-500' : 'text-brand-navy/50'}`} />
                  {item.label}
                </button>
              )
            )}
          </div>
        </div>
      </div>

      {loggingOut && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <GlobalLoader size="lg" message="Logging out…" />
        </div>
      )}
      <ProfileModal isOpen={profileOpen} onClose={() => setProfileOpen(false)} />
      <ChangePasswordModal isOpen={passwordOpen} onClose={() => setPasswordOpen(false)} />
      <SettingsModal isOpen={settingsOpen} onClose={() => setSettingsOpen(false)} />
    </>
  );
}
